import { ChangeEvent, FocusEvent, KeyboardEvent, WheelEvent } from 'react'

import { isWithinNumericLimit } from '@/utils/numericFieldLimits'

type NumericInputElement = HTMLInputElement | HTMLTextAreaElement

// O elemento do evento fica genérico porque os campos passam por componentes com assinaturas
// diferentes (TextField do @nexds, UnitField, register do react-hook-form).
interface NumericInputHandlers {
  onChange?: (event: ChangeEvent<any>) => void
  onFocus?: (event: FocusEvent<any>) => void
  onKeyDown?: (event: KeyboardEvent<any>) => void
  onWheel?: (event: WheelEvent<any>) => void
}

interface NumericFieldLimitOptions {
  /** Limite da grandeza do campo — use as constantes de `NUMERIC_FIELD_LIMITS`. */
  max: number
  /** Campos de braço, momento e %CMA aceitam valores negativos; peso e combustível, não. */
  allowNegative?: boolean
}

/**
 * Último valor aceito de cada campo, para onde a digitação é revertida quando passa do limite.
 *
 * Fica no próprio elemento porque vários desses campos são renderizados dentro de listas (estações do
 * preenchimento, braços variáveis do tanque), onde não há como guardar o valor em um hook.
 */
const lastAcceptedValueByInput = new WeakMap<NumericInputElement, string>()

const BLOCKED_KEYS = new Set(['e', 'E', '+'])

/**
 * Acrescenta o limite máximo a um campo numérico, mantendo os handlers que ele já tinha.
 *
 * O valor que ultrapassaria o limite é recusado na hora: a digitação (ou o colar) é revertida para o
 * último valor aceito e o `onChange` original não é chamado, então o formulário nunca chega a guardar
 * um valor acima do limite. A verificação acontece depois da alteração, e não na tecla, porque
 * `input[type=number]` não expõe a posição do cursor — assim selecionar tudo e redigitar, colar e usar
 * as setinhas do campo funcionam igual.
 *
 * @example
 * <TextField {...withNumericLimit(register('basicEmptyWeight', { valueAsNumber: true }), { max: NUMERIC_FIELD_LIMITS.weight })} />
 */
export function withNumericLimit<T extends NumericInputHandlers>(
  props: T,
  { max, allowNegative = false }: NumericFieldLimitOptions
) {
  return {
    ...props,
    type: 'number' as const,
    min: allowNegative ? undefined : '0',
    max,
    onWheel: (event: WheelEvent<any>) => {
      const input: NumericInputElement = event.currentTarget
      input.blur()
      props.onWheel?.(event)
    },
    onKeyDown: (event: KeyboardEvent<any>) => {
      // Notação científica e sinal explícito de positivo não fazem sentido nestes campos, e o negativo
      // só nos que aceitam valor à frente do datum.
      if (BLOCKED_KEYS.has(event.key) || (!allowNegative && event.key === '-')) {
        event.preventDefault()
        return
      }
      props.onKeyDown?.(event)
    },
    onFocus: (event: FocusEvent<any>) => {
      const input: NumericInputElement = event.currentTarget
      lastAcceptedValueByInput.set(input, input.value)
      props.onFocus?.(event)
    },
    onChange: (event: ChangeEvent<any>) => {
      const input: NumericInputElement = event.currentTarget

      if (!isWithinNumericLimit(input.value, max)) {
        // O React rastreia o valor do campo pelo setter da instância, então atribuir aqui mantém o
        // rastreio em dia: a próxima digitação continua disparando `onChange` normalmente.
        input.value = lastAcceptedValueByInput.get(input) ?? ''
        return
      }

      lastAcceptedValueByInput.set(input, input.value)
      props.onChange?.(event)
    }
  }
}
